import { Audio } from '@remotion/media'
import { useMemo } from 'react'
import { Sequence, useVideoConfig } from 'remotion'
import { useAssets, useMediaAsset } from '../contexts'
import { volumeFn } from '../fades'
import { itemFramePieces } from '../sceneWindow'
import { MEDIA_PREMOUNT_SECONDS } from './premount'
import type { AudioItem } from '../types'

export function AudioLayer({
  item,
  trackMuted,
}: {
  item: AudioItem
  trackMuted: boolean
}) {
  const { fps } = useVideoConfig()
  const asset = useMediaAsset(item.assetId)
  const assets = useAssets()
  // The kept parts of the scene's window (#77): one piece without a cut.
  const pieces = useMemo(
    () => itemFramePieces(item, assets, fps),
    [item, assets, fps],
  )

  const volume = useMemo(
    () =>
      volumeFn({
        fps,
        audioFadeInDurationInSeconds: item.audioFadeInDurationInSeconds,
        audioFadeOutDurationInSeconds: item.audioFadeOutDurationInSeconds,
        durationInFrames: item.durationInFrames,
        decibelAdjustment: item.decibelAdjustment,
      }),
    [
      fps,
      item.audioFadeInDurationInSeconds,
      item.audioFadeOutDurationInSeconds,
      item.durationInFrames,
      item.decibelAdjustment,
    ],
  )

  // An audio item over a video asset plays the audio-only rendition when one
  // is recorded, the file itself otherwise (root AGENTS.md rendition table).
  const src =
    (asset.type === 'video' || asset.type === 'audio'
      ? asset.audioUrl
      : null) ?? asset.remoteUrl
  if (!src) throw new Error(`Asset ${asset.id} has no URL`)

  const audio = (trimBefore: number, offset: number) => (
    <Audio
      trimBefore={trimBefore}
      src={src}
      // Fades are the item's: a piece's frames count from its own start.
      volume={offset ? (f: number) => volume(f + offset) : volume}
      playbackRate={item.playbackRate}
      muted={trackMuted}
    />
  )

  if (pieces.length === 1) return audio(pieces[0]!.trimBefore, 0)
  return (
    <>
      {pieces.map((piece) => (
        // Premounted, so the next piece is loaded before the cut.
        <Sequence
          key={piece.from}
          from={piece.from}
          durationInFrames={piece.durationInFrames}
          premountFor={MEDIA_PREMOUNT_SECONDS * fps}
        >
          {audio(piece.trimBefore, piece.from)}
        </Sequence>
      ))}
    </>
  )
}
